// models/posts/postFeedModels.js
//
// A post can live in more than one ORM table at once: `Post` (the home feed,
// a post's own page), `Bookmark` (the bookmarks tab) and `TadbeesaPost` (the
// /tadbeesa/ page). Each is a separate table ON PURPOSE — a feed's reset
// flushes only its own table — so a card must read from the table its feed
// wrote, and a change to one post must reach every table that holds it.
//
// Two rules this module owns, so no component re-derives them:
//
//  1. READ from the feed's own table (`postModelForFeed`). A card on the
//     bookmarks tab or the تدبيسة page that read `Post` would find nothing, or
//     a stale copy.
//
//  2. PATCH only where the row ALREADY exists (`patchHeldPost`). pinia-orm's
//     `save` is an upsert: a blind `useRepo(Post).save({ id, isLiked })` for a
//     post the home feed never loaded INSERTS a row with no channel, media or
//     product — and the home feed, which lists every `Post` row, renders it as
//     an empty ghost card. ProductSuggestionHost learned this first; the like /
//     bookmark toggles now follow the same rule, and the cart's post refetch
//     (useCart.fetchPostData) follows it for every table except `Post`, which
//     it still upserts because ItemSheets reads the post back from there.
import { useRepo } from 'pinia-orm';
import Bookmark from '@/models/Bookmark.js';
import Post from '@/models/posts/Post.js';
import TadbeesaPost from '@/models/posts/TadbeesaPost.js';

/** Every table a post card can be reading from. `Post` first: it is the one
 *  most surfaces write (a post's own page, the cart's refetch). */
export const POST_FEED_MODELS = [Post, TadbeesaPost, Bookmark];

/**
 * The table a card on `feed` reads. Unknown feeds (home, oos, a post's own
 * page) read `Post`, which is what they all write.
 *
 * @param {string} feed - The PostCard `currentFeed` key.
 */
export function postModelForFeed(feed) {
  if (feed === 'bookmarks') return Bookmark;
  if (feed === 'tadbeesa') return TadbeesaPost;
  return Post;
}

/**
 * The first held copy of a post, from whichever table has it, or null.
 *
 * For surfaces that are handed only a post id and cannot know which feed they
 * were opened from (the add-to-carton popup, the share button).
 *
 * @param {string|number} postId
 * @param {(repo: import('pinia-orm').Repository) => any} [scope] - Adds eager
 *   loads, e.g. `(repo) => repo.with('media')`.
 */
export function findHeldPost(postId, scope = (repo) => repo) {
  if (postId == null) return null;
  for (const model of POST_FEED_MODELS) {
    const found = scope(useRepo(model)).find(postId);
    if (found) return found;
  }
  return null;
}

/**
 * Write `record` into every table that already holds this post — and into no
 * other. `record` must carry the post `id`; it can be a partial patch
 * (`{ id, isLiked }`) or a full refetched post.
 *
 * @param {{ id: string|number }} record
 * @param {Array} [models=POST_FEED_MODELS] - Narrow the tables, e.g. to skip
 *   one the caller has already saved into.
 */
export function patchHeldPost(record, models = POST_FEED_MODELS) {
  if (record?.id == null) return;
  for (const model of models) {
    const repo = useRepo(model);
    if (repo.find(record.id)) repo.save(record);
  }
}
